import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ConfigurationPanel from './components/ConfigurationPanel';
import CodeGenerator from './components/CodeGenerator';
import AdvancedSettings from './components/AdvancedSettings';
import TestingTools from './components/TestingTools';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorState from '../../components/ui/ErrorState';
import { catalogService } from '../../services/catalogService';
import { embedService } from '../../services/embedService';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const EmbedSettings = () => {
  const { catalogId } = useParams();
  const navigate = useNavigate();
  const { userProfile, activeCompanyId } = useAuth();
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  const [config, setConfig] = useState({
    width: '100%',
    height: '800',
    responsive: true,
    initialPage: 1,
    theme: 'light',
    showNavigation: true,
    showSharing: false,
    showZoom: true,
    showFullscreen: true,
    showDownload: false,
    showMenu: false,
    showSearch: false,
    defaultLanguage: 'es',
    viewerMode: 'flip',
    preset: 'custom'
  });

  const [advancedConfig, setAdvancedConfig] = useState({
    allowedDomains: '',
    requireReferrer: false,
    customCSS: '',
    primaryColor: '',
    enableAnalytics: true,
    ga4Id: '',
    trackHotspots: true,
    lazyLoad: true,
    preloadAdjacent: true
  });

  useEffect(() => {
    if (!catalogId) {
      setError('No catalog ID provided');
      setLoading(false);
      return;
    }
    
    fetchCatalogData();
  }, [catalogId]);

  const fetchCatalogData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const catalogData = await catalogService?.getCatalogById(catalogId);
      
      if (!catalogData) {
        throw new Error('Catalog not found');
      }
      
      setCatalog(catalogData);

      // Load existing embed configuration
      const embedConfig = await embedService?.getEmbedConfig(catalogId);
      
      if (embedConfig) {
        // Populate config from database
        setConfig({
          width: embedConfig?.width || '100%',
          height: embedConfig?.height || '800',
          responsive: embedConfig?.responsive !== false,
          initialPage: embedConfig?.initial_page || 1,
          theme: embedConfig?.theme || 'light',
          showNavigation: embedConfig?.show_navigation !== false,
          showSharing: embedConfig?.show_sharing || false,
          showZoom: embedConfig?.show_zoom !== false,
          showFullscreen: embedConfig?.show_fullscreen !== false,
          showDownload: embedConfig?.show_download || false,
          showMenu: embedConfig?.show_menu || false,
          showSearch: embedConfig?.show_search || false,
          defaultLanguage: embedConfig?.default_language || 'es',
          viewerMode: embedConfig?.viewer_mode || 'flip',
          preset: 'custom'
        });

        setAdvancedConfig({
          allowedDomains: embedConfig?.allowed_domains || '',
          requireReferrer: embedConfig?.require_referrer || false,
          customCSS: embedConfig?.custom_css || '',
          primaryColor: embedConfig?.primary_color || '',
          enableAnalytics: embedConfig?.enable_analytics !== false,
          ga4Id: embedConfig?.ga4_id || '',
          trackHotspots: embedConfig?.track_hotspots !== false,
          lazyLoad: embedConfig?.lazy_load !== false,
          preloadAdjacent: embedConfig?.preload_adjacent !== false
        });
      }
    } catch (err) {
      console.error('Error fetching catalog:', err);
      setError(err?.message || 'Failed to load catalog data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfiguration = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      setError(null);

      // catalog.companyId is camelCase (not snake_case); activeCompanyId is the last fallback for super_admin
      const companyId = userProfile?.company_id || catalog?.companyId || activeCompanyId;

      if (!companyId) {
        throw new Error('Company ID not found');
      }

      // Merge basic and advanced config
      const fullConfig = {
        ...config,
        ...advancedConfig
      };

      await embedService?.saveEmbedConfig(catalogId, companyId, fullConfig);
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving configuration:', err);
      setError(err?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-background overflow-hidden">
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
        <main
          className={`flex-1 overflow-y-auto transition-all duration-250 ${
            sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-[280px]'
          }`}
        >
          <div className="flex items-center justify-center h-full">
            <LoadingSpinner size="lg" />
          </div>
        </main>
      </div>
    );
  }

  if (error && !catalog) {
    return (
      <div className="flex h-screen bg-background overflow-hidden">
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
        <main
          className={`flex-1 overflow-y-auto transition-all duration-250 ${
            sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-[280px]'
          }`}
        >
          <div className="flex items-center justify-center h-full p-6">
            <ErrorState
              title={t('embedSettings.catalogNotFound')}
              message={error || t('embedSettings.catalogNotFoundMsg')}
              actionLabel={t('embedSettings.backToDashboard')}
              onAction={() => navigate('/dashboard')}
              onRetry={fetchCatalogData}
            />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
      <main
        className={`flex-1 overflow-y-auto transition-all duration-250 ${
          sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-[280px]'
        }`}
      >
        <div className="min-h-full">
          <div className="sticky top-0 z-30 bg-card border-b border-border px-4 md:px-6 lg:px-8 py-4 md:py-5 lg:py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-1 md:mb-2">
                  {t('embedSettings.title')}
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  {t('embedSettings.subtitle')} <span className="font-medium text-foreground">{catalog?.title}</span>
                </p>
              </div>
              <div className="flex items-center gap-3">
                {saveSuccess && (
                  <div className="flex items-center gap-2 text-success text-sm">
                    <Icon name="CheckCircle" size={16} />
                    <span>{t('embedSettings.saved')}</span>
                  </div>
                )}
                <Button
                  variant="primary"
                  size="md"
                  iconName="Save"
                  iconPosition="left"
                  onClick={handleSaveConfiguration}
                  disabled={saving}
                >
                  {saving ? t('embedSettings.saving') : t('embedSettings.save')}
                </Button>
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="hidden lg:flex items-center justify-center w-10 h-10 rounded-md hover:bg-muted transition-base"
                  aria-label={t('embedSettings.toggleSidebar')}
                >
                  <Icon name={sidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} size={20} />
                </button>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
                  <div>
                    <p className="font-medium text-destructive">{t('common.error')}</p>
                    <p className="text-sm text-muted-foreground mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-4 md:gap-6 lg:gap-8 mb-6 md:mb-8">
              <ConfigurationPanel config={config} onConfigChange={setConfig} />
            </div>
            <div className="mb-6 md:mb-8">
              <CodeGenerator config={config} catalog={catalog} />
            </div>
            {/*
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-4 md:gap-6 lg:gap-8 mb-6 md:mb-8">
              <AdvancedSettings
                advancedConfig={advancedConfig}
                onAdvancedConfigChange={setAdvancedConfig}
              />
            </div>
            */}

            <div className="mb-6 md:mb-8">
              <TestingTools catalogId={catalogId} />
            </div>

            <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
              <div className="flex items-start gap-3 md:gap-4">
                <Icon name="BookOpen" size={20} className="text-accent mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
                    {t('embedSettings.guide.title')}
                  </h3>
                  <div className="space-y-3 md:space-y-4 text-sm md:text-base text-muted-foreground">
                    <div>
                      <p className="font-medium text-foreground mb-1">{t('embedSettings.guide.step1Title')}</p>
                      <p>{t('embedSettings.guide.step1Desc')}</p>
                    </div>
                    <div>
                      <p className="font-medium text-foreground mb-1">{t('embedSettings.guide.step2Title')}</p>
                      <p>{t('embedSettings.guide.step2Desc')}</p>
                    </div>
                    <div>
                      <p className="font-medium text-foreground mb-1">{t('embedSettings.guide.step3Title')}</p>
                      <p>{t('embedSettings.guide.step3Desc')}</p>
                    </div>
                    <div>
                      <p className="font-medium text-foreground mb-1">{t('embedSettings.guide.step4Title')}</p>
                      <p>{t('embedSettings.guide.step4Desc')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <footer className="border-t border-border px-4 md:px-6 lg:px-8 py-4 md:py-6 bg-card">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 md:gap-4">
              <p className="text-xs md:text-sm text-muted-foreground text-center sm:text-left">
                © {new Date()?.getFullYear()} CatalogFlip. {t('embedSettings.footer.rights')}
              </p>
              <div className="flex items-center gap-4 md:gap-6">
                <a href="#" className="text-xs md:text-sm text-muted-foreground hover:text-foreground transition-base">
                  {t('embedSettings.footer.documentation')}
                </a>
                <a href="#" className="text-xs md:text-sm text-muted-foreground hover:text-foreground transition-base">
                  {t('embedSettings.footer.support')}
                </a>
                <a href="#" className="text-xs md:text-sm text-muted-foreground hover:text-foreground transition-base">
                  {t('embedSettings.footer.apiReference')}
                </a>
              </div>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default EmbedSettings;